//#region libraries
import React, { useEffect, useState } from 'react';

// custom components
import { Card, CustomSpinner } from "_atoms";
import { Clicker } from "_molecules";
import { CustomTooltip } from "_organisms";

// bootstrap
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// redux
import { selectIata } from "_redux/slices/main-screen/user";
import {
  getAnalyticsCountInbound,
  getAnalyticsCountOutbound,
  getAnalyticsStats,
  getAnalyticsStatsDayComparedToLastWeek,
  selectAnalyticsStats,
  selectAnalyticsStatsDayComparedToLastWeek,
  selectAnalyticsTypeSelector,
  selectDeparturesArrivalsCount,
  selectWeeklyAnalyticsIsLoading,
  setTypeSelector
} from "_redux/slices/prm-bookings/bookings-count";
import { useDispatch, useSelector } from "react-redux";

import { selectUserDb } from "_redux/slices/main-screen/user";

import { selectDateToISO, setDate } from "_redux/slices/prm-bookings/calendar";
// tremor librarues
import {
  BadgeDelta,
  BarChart,
  ColGrid,
  Dropdown,
  DropdownItem,
  Flex,
  Metric,
  Subtitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
  Title,
  Col as TremorCol
} from "@tremor/react";

// utils
import { lookupIsLoading } from "_utils/statusUtil";

// icons
import { selectAirlineSelectedAirport } from "_redux/slices/prm-bookings/bookings";
import { HiOutlineInformationCircle } from "react-icons/hi";
//#endregion

//#region components
const dataFormatter = (number) => {
  return Intl.NumberFormat("us").format(number).toString();
};

const CustomCard = (props) => {
  let count = parseInt(props.text, 10);

  return (
    <Card maxWidth="max-w-lg">
      <Flex marginTop="mt-4" alignItems="items-start">
        <CustomTooltip
          text={
            props.isSameWeek
              ? "The percentage change in the total number of " +
              props.title +
              " bookings from the compared to " +
              props.isSameWeek
              : "The percentage change in the total number of " +
              props.title +
              " bookings from the previous week"
          }
        >
          <div style={{ display: "flex" }}>
            <p>
              <HiOutlineInformationCircle size={20} color="#307FE2" />
              <Text>{props.title}</Text>
            </p>
          </div>
        </CustomTooltip>
        {props.isLoading ? (
          <CustomSpinner />
        ) : (
          <Metric>{props.data.toLocaleString()}</Metric>
        )}

        <BadgeDelta
          deltaType={count < 0 ? "decrease" : "increase"}
          text={props.text}
        />
      </Flex>
    </Card>
  );
};
//#endregion

//#region functios
// get monday of week
const mondayThisWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

// get offset date
const offsetDate = (base, count, type = "", source = "") => {
  base = new Date(base);
  let date = new Date(base);
  date.setDate(base.getDate() + count);
  return date;
};
//#endregion

const WeeklyAnalytics = (props) => {
  //#region redux
  const dispatch = useDispatch();
  const analyticsDate = useSelector(selectDateToISO); // start date of the week
  const analyticsTypeSelector = useSelector(selectAnalyticsTypeSelector); // 'source' (app/sita/portal) or 'none' (all bookings)
  const splitData = useSelector(selectDeparturesArrivalsCount);
  const analyticsStats = useSelector(selectAnalyticsStats);
  const analyticsStatsDayComparedToLastWeek = useSelector(
    selectAnalyticsStatsDayComparedToLastWeek
  );
  const userDb = useSelector(selectUserDb);
  const isLoading = useSelector(selectWeeklyAnalyticsIsLoading);
  const lastWeek = new Date(new Date().setDate(new Date().getDate() - 7))
    .toISOString()
    .split("T")[0];

  const iata = useSelector(selectIata);
  const airport = useSelector(selectAirlineSelectedAirport);
  //#endregion

  //#region states
  const [startDateString, setStartDateString] = useState("");
  const [endDateString, setEndDateString] = useState("");

  //#endregion

  //#region isLoading variables
  const analyticsStatsIsLoading = useSelector((state) =>
    lookupIsLoading(state.count.statusObj, ["getAnalyticsStats"])
  );

  //#endregion

  const currentWeek = () => {
    return (
      Math.abs((new Date(analyticsDate) - new Date()) / (24 * 60 * 60 * 1000)) <
      7
    );
  };

  useEffect(() => {
    let countParams = {
      iata: userDb.details.type == "AIRLINE" ? airport : iata,
      date: analyticsDate,
      range: "week",
      source: analyticsTypeSelector,
      airline:
        userDb.details.type == "AIRLINE" ? userDb.details.authority : null,
    };

    dispatch(getAnalyticsCountInbound(countParams));
    dispatch(getAnalyticsCountOutbound(countParams));
    dispatch(getAnalyticsStats(countParams));
    dispatch(getAnalyticsStatsDayComparedToLastWeek(countParams));

    const monday = mondayThisWeek(analyticsDate);
    const sunday = offsetDate(monday, 6);
    setStartDateString(monday.toLocaleDateString());
    setEndDateString(sunday.toLocaleDateString());
  }, [analyticsDate, airport, analyticsTypeSelector]);

  // change start date to next monday
  const handleNextWeek = () => {
    if (!isLoading) {
      const newMonday = offsetDate(analyticsDate, 7);
      dispatch(setDate(newMonday.toISOString().split("T")[0]));
    }
  };

  // change start date to previous monday
  const handlePreviousWeek = () => {
    if (!isLoading) {
      const newMonday = offsetDate(analyticsDate, -7);
      dispatch(setDate(newMonday.toISOString().split("T")[0]));
    }
  };

  const handleSelect = (value) => {
    dispatch(setTypeSelector(value));
  };
  //#endregion

  return (
    <div style={{ opacity: isLoading ? 0.6 : 1 }}>
      <Row className="mt-4">
        <Col xs={3}>
          <Clicker
            text={
              startDateString.substring(0, 5) +
              " - " +
              endDateString.substring(0, 5)
            }
            onClickPrevious={handlePreviousWeek}
            onClickNext={handleNextWeek}
          />
        </Col>
      </Row>

      {!currentWeek() && analyticsStats && (
        /* Statistic Tabs */
        <ColGrid
          numColsMd={1}
          numColsLg={3}
          gapX="gap-x-6"
          gapY="gap-y-6"
          marginTop="mt-6"
        >
          <CustomCard
            title={"Departures"}
            data={analyticsStats.outbound}
            text={`${analyticsStats.outboundDiff
                ? analyticsStats.outboundDiff.toFixed(1)
                : 0
              }%`}
          // isLoading={analyticsStatsIsLoading}
          />
          <CustomCard
            title={"Arrivals"}
            data={analyticsStats.inbound}
            text={`${analyticsStats.inboundDiff
                ? analyticsStats.inboundDiff.toFixed(1)
                : 0
              }%`}
          // isLoading={analyticsStatsIsLoading}
          />
          <CustomCard
            title={"Total"}
            data={analyticsStats.total}
            text={`${analyticsStats.totalDiff ? analyticsStats.totalDiff.toFixed(1) : 0
              }%`}
          // isLoading={analyticsStatsIsLoading}
          />
        </ColGrid>
      )}

      {currentWeek() &&
        analyticsStats &&
        analyticsStatsDayComparedToLastWeek && (
          /* Statistic Tabs */
          <ColGrid
            numColsMd={1}
            numColsLg={3}
            gapX="gap-x-6"
            gapY="gap-y-6"
            marginTop="mt-6"
          >
            {/*Some text containing the words 'comparing today to last week*/}
            <CustomCard
              isSameWeek={lastWeek}
              title={"Departures"}
              data={analyticsStats && analyticsStats.outbound}
              text={
                analyticsStatsDayComparedToLastWeek &&
                `${analyticsStatsDayComparedToLastWeek.outboundDiff
                  ? analyticsStatsDayComparedToLastWeek.outboundDiff.toFixed(
                    1
                  )
                  : 0
                }% (${analyticsStatsDayComparedToLastWeek.lastWeekOutbound} / ${analyticsStatsDayComparedToLastWeek.thisWeekOutbound
                }) `
              }
            //   isLoading={
            //     analyticsStatsIsLoading ||
            //     analyticsStatsDayComparedToLastWeekIsLoading
            //   }
            />
            <CustomCard
              isSameWeek={lastWeek}
              title={"Arrivals"}
              data={analyticsStats && analyticsStats.inbound}
              text={
                analyticsStatsDayComparedToLastWeek &&
                `${analyticsStatsDayComparedToLastWeek.inboundDiff
                  ? analyticsStatsDayComparedToLastWeek.inboundDiff.toFixed(1)
                  : 0
                }% (${analyticsStatsDayComparedToLastWeek.lastWeekInbound} / ${analyticsStatsDayComparedToLastWeek.thisWeekInbound
                })`
              }
            //   isLoading={
            //     analyticsStatsIsLoading ||
            //     analyticsStatsDayComparedToLastWeekIsLoading
            //   }
            />
            <CustomCard
              isSameWeek={lastWeek}
              title={"Total"}
              data={analyticsStats && analyticsStats.total}
              text={
                analyticsStatsDayComparedToLastWeek &&
                `${analyticsStatsDayComparedToLastWeek.totalDiff
                  ? analyticsStatsDayComparedToLastWeek.totalDiff.toFixed(1)
                  : 0
                }% (${analyticsStatsDayComparedToLastWeek.lastWeekOutbound +
                analyticsStatsDayComparedToLastWeek.lastWeekInbound
                } / ${analyticsStatsDayComparedToLastWeek.thisWeekOutbound +
                analyticsStatsDayComparedToLastWeek.thisWeekInbound
                })`
              }
            //   isLoading={
            //     analyticsStatsIsLoading ||
            //     analyticsStatsDayComparedToLastWeekIsLoading
            //   }
            />
          </ColGrid>
        )}

      <ColGrid
        numColsMd={1}
        numColsLg={6}
        gapX="gap-x-6"
        gapY="gap-y-6"
        marginTop="mt-6"
      >
        {/* Graph */}
        <TremorCol numColSpanLg={4}>
          <Card>
            <Row>
              <Col>
                <Title>Weekly Bookings</Title>
                <Subtitle>View the bookings for the current week</Subtitle>
              </Col>
              <Col>
                <Dropdown
                  placeholder="All Bookings"
                  defaultValue={analyticsTypeSelector}
                  onValueChange={handleSelect}
                  maxWidth="max-w-none"
                >
                  <DropdownItem value={"none"} text="All Bookings" Icon="" />
                  <DropdownItem value={"P"} text="Portal" Icon="" />
                  <DropdownItem value={"A"} text="App" Icon="" />
                  <DropdownItem value={"S"} text="SITA" Icon="" />
                </Dropdown>
              </Col>
            </Row>
            <Row>
              {
                /* {isLoading && */
                // splitDataIsLoading ? (
                //   <Row className="justify-content-md-center mt-3">
                //     <CustomSpinner />
                //   </Row>
                // ) : (
                <BarChart
                  data={splitData}
                  dataKey="name"
                  categories={["Departures", "Arrivals"]}
                  colors={["blue", "green"]}
                  marginTop="mt-6"
                  yAxisWidth="w-12"
                  valueFormatter={dataFormatter}
                />
                // )
              }
            </Row>
          </Card>
        </TremorCol>

        {/* Assistance Breakdown Table */}
        <TremorCol numColSpanLg={2}>
          <div style={{ opacity: analyticsStatsIsLoading ? 0.6 : 1 }}>
            <Card maxWidth="max-w-lg">
              <Title>Request Type</Title>
              <Table>
                {/* Headers */}
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Request</TableHeaderCell>
                    <TableHeaderCell>Departure</TableHeaderCell>
                    <TableHeaderCell>Arrival</TableHeaderCell>
                    <TableHeaderCell>Total</TableHeaderCell>
                  </TableRow>
                </TableHead>

                {/* Body */}
                {analyticsStats && analyticsStats.assistanceTable ? (
                  <TableBody>
                    {analyticsStats.assistanceTable &&
                      analyticsStats.assistanceTable.map((item) => (
                        <TableRow>
                          <TableCell>{item.key}</TableCell>
                          <TableCell>{item.outbound}</TableCell>
                          <TableCell>{item.inbound}</TableCell>
                          <TableCell>{item.inbound + item.outbound}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                ) : null}
              </Table>
            </Card>
          </div>
        </TremorCol>
      </ColGrid>
    </div>
  );
};

export default WeeklyAnalytics;
